import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { GlobalOutlined, MoreOutlined } from '@ant-design/icons';
import type { InfiniteData } from '@tanstack/react-query';
import { Dropdown, Tabs } from 'antd';
import type { MenuProps, TabsProps } from 'antd';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

import type { OpenMetadataResponse } from '@/shared/api/openmetadata';
import { getAssetTypeName } from '@/shared/api/openmetadata';
import { COLOR } from '@/shared/design-tokens/design-tokens';
import { useAuthorization } from '@/shared/hooks';
import { NOTIFICATION_MESSAGES, TREE_DEPTH } from '@/shared/lib/constants';
import { calculateFqnDepth } from '@/shared/lib/format/fqn';
import { sortByDisplayName } from '@/shared/lib/sort/sortByDisplayName';
import { Button } from '@/shared/ui/Button';
import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { Icon } from '@/shared/ui/Icon';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';
import { LoadingState } from '@/shared/ui/states/LoadingState';
import { SuspenseFallback } from '@/shared/ui/SuspenseFallback';

import { useFilterStore } from '@/entities/discovery';
import type { DomainItem } from '@/entities/domain';

import { DomainFormModal } from '@/features/domain';
import { useDataAssets, useDeleteDomain, useDomainDetail, useSubDomains } from '@/features/domain/model';

import { DataAssetsTab } from '@/widgets/domain/DataAssetsTab';
import { DomainDetailSidebar } from '@/widgets/domain/DomainDetailSidebar';
import { SubDomainsTab } from '@/widgets/domain/SubDomainsTab';

import styles from './DomainDetailContent.module.css';

type DomainDetailContentProps = {
  domainFqn: string | null;
  currentTab: string;
  breadcrumbItems?: BreadcrumbItemType[];
  onDomainRefresh?: (parentFqn: string) => void;
  onDomainDelete?: (parentFqn: string) => void;
  onDomainDetailLoaded?: (displayName: string) => void;
};

const Main: React.FC<DomainDetailContentProps> = ({
  domainFqn,
  currentTab,
  breadcrumbItems,
  onDomainRefresh,
  onDomainDelete,
  onDomainDetailLoaded,
}) => {
  const navigate = useNavigate();
  const { filters } = useFilterStore();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddSubDomainModalOpen, setIsAddSubDomainModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { data: domainDetail, isLoading } = useDomainDetail(domainFqn, {
    enabled: !!domainFqn,
  });

  const { handleDelete, isPending: isDeleting } = useDeleteDomain(domainFqn || undefined, {
    onTreeRefresh: onDomainRefresh,
    onDeleteStart: onDomainDelete,
  });

  useEffect(() => {
    if (domainDetail) {
      const displayName = domainDetail.displayName || domainDetail.name || '';
      onDomainDetailLoaded?.(displayName);
    }
  }, [domainDetail, onDomainDetailLoaded]);

  const { hasAnyRole } = useAuthorization();
  const canManageCategories = hasAnyRole(['admin', 'manager']);

  const canAddSubDomain = useMemo(() => {
    if (!domainFqn) return true;
    const currentDepth = calculateFqnDepth(domainFqn);
    return currentDepth < TREE_DEPTH.DOMAIN;
  }, [domainFqn]);

  const { data: subDomainsData, isLoading: isSubDomainsLoading } = useSubDomains({
    parentFqn: domainFqn || '',
    enabled: !!domainFqn,
  });

  const subDomains = useMemo<DomainItem[]>(() => {
    if (!subDomainsData?.hits) return [];
    const domains = subDomainsData.hits.hits.map((hit: { _source: DomainItem }) => hit._source);
    return sortByDisplayName(domains);
  }, [subDomainsData]);

  const subDomainsCount = subDomainsData?.hits?.total.value || 0;

  const { data: assetsData } = useDataAssets({
    domainFqn: domainFqn || '',
    filters,
    enabled: !!domainFqn,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });

  const assetsCount = useMemo(() => {
    const typedData = assetsData as InfiniteData<OpenMetadataResponse> | undefined;
    return typedData?.pages?.[0]?.hits?.total?.value || 0;
  }, [assetsData]);

  const handleTabChange = useCallback(
    (key: string) => {
      if (domainFqn) {
        navigate(`/domain/${encodeURIComponent(domainFqn)}/${key}`, { replace: true });
      }
    },
    [domainFqn, navigate]
  );

  const handleEditClick = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const handleEditSuccess = useCallback(
    (parentFqn: string) => {
      onDomainRefresh?.(parentFqn);
    },
    [onDomainRefresh]
  );

  const handleOpenAddSubDomainModal = useCallback(() => {
    setIsAddSubDomainModalOpen(true);
  }, []);

  const handleCloseAddSubDomainModal = useCallback(() => {
    setIsAddSubDomainModalOpen(false);
  }, []);

  const handleAddSubDomainSuccess = useCallback(
    (parentFqn: string) => {
      onDomainRefresh?.(parentFqn);
    },
    [onDomainRefresh]
  );

  const handleDeleteClick = useCallback(() => {
    if (!domainDetail) return;
    setDeleteConfirmOpen(true);
  }, [domainDetail]);

  const handleDeleteExecute = useCallback(async () => {
    if (!domainDetail) return;
    await handleDelete(domainDetail.id);
  }, [domainDetail, handleDelete]);

  const deleteMessage = useMemo(() => {
    if (!domainDetail) return '';
    return `"${domainDetail.displayName || domainDetail.name}"을(를) 삭제하시겠습니까?\n하위 도메인도 함께 삭제됩니다.`;
  }, [domainDetail]);

  const dropdownItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'edit',
        label: '수정',
        icon: <Icon name="edit" size={16} />,
        onClick: handleEditClick,
      },
      {
        key: 'delete',
        label: '삭제',
        icon: <Icon name="trash" width={13.6} />,
        onClick: handleDeleteClick,
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  const createTabLabel = (label: string, count: number, isActive: boolean) => (
    <div className={styles.tabLabel}>
      <span className={styles.textLabel}>{label}</span>
      <span className={`${styles.tabBadge} ${isActive ? styles.tabBadgeActive : ''}`}>{count}</span>
    </div>
  );

  const tabItems: TabsProps['items'] = useMemo(
    () => [
      {
        key: 'sub-domains',
        label: createTabLabel('하위 도메인', subDomainsCount, currentTab === 'sub-domains'),
        children: <SubDomainsTab subDomains={subDomains} isLoading={isSubDomainsLoading} />,
      },
      {
        key: 'assets',
        label: createTabLabel('자산', assetsCount, currentTab === 'assets'),
        children: domainFqn ? <DataAssetsTab domainFqn={domainFqn} /> : null,
      },
    ],
    [subDomains, subDomainsCount, isSubDomainsLoading, assetsCount, currentTab, domainFqn]
  );

  if (isLoading) {
    return <LoadingState message="도메인 정보를 불러오는 중..." />;
  }

  const displayName = domainDetail?.displayName || domainDetail?.name || '';

  return (
    <div className={styles.contentWrapper}>
      <NoDataBoundary data={domainDetail} emptyDescription="도메인을 선택해주세요">
        <ContentHeader
          breadcrumbItems={breadcrumbItems}
          icon={{
            component: (
              <GlobalOutlined className="icon" style={{ color: 'var(--opds-sem-color-onContainer-standard-level1)' }} />
            ),
            tooltip: getAssetTypeName('domain'),
          }}
          title={displayName}
          description={domainDetail?.description}
          actions={
            <>
              {canAddSubDomain && canManageCategories && (
                <Button prefix={<Icon name="add" size={16} />} onClick={handleOpenAddSubDomainModal}>
                  하위 도메인 추가
                </Button>
              )}
              {canManageCategories && (
                <Dropdown
                  menu={{ items: dropdownItems }}
                  trigger={['click']}
                  disabled={isDeleting}
                  rootClassName={styles.dropdown}>
                  <button type="button" className={styles.moreButton}>
                    <MoreOutlined size={16} color={COLOR.GRAY[10]} />
                  </button>
                </Dropdown>
              )}
            </>
          }
        />

        <div className={styles.mainContent}>
          <div className={styles.contentArea}>
            <Tabs activeKey={currentTab} onChange={handleTabChange} items={tabItems} destroyOnHidden />
          </div>
          <div className={styles.sidebarArea}>
            {domainDetail && <DomainDetailSidebar domainDetail={domainDetail} />}
          </div>
        </div>
      </NoDataBoundary>

      <DomainFormModal
        open={isEditModalOpen}
        mode="edit"
        domainData={domainDetail}
        domainFqn={domainFqn || undefined}
        onClose={handleEditModalClose}
        onSuccess={handleEditSuccess}
      />

      {domainDetail && (
        <DomainFormModal
          open={isAddSubDomainModalOpen}
          mode="create"
          parentInfo={{
            parentFqn: domainFqn || undefined,
            parentName: domainDetail.displayName || domainDetail.name,
          }}
          onClose={handleCloseAddSubDomainModal}
          onSuccess={handleAddSubDomainSuccess}
        />
      )}

      <ConfirmModal
        open={deleteConfirmOpen}
        message={deleteMessage}
        confirmText="삭제"
        cancelText="취소"
        confirmButtonProps={{ danger: true }}
        onConfirm={handleDeleteExecute}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
};

const Suspense = () => {
  return <SuspenseFallback />;
};

const Error = (props: React.ComponentProps<typeof ErrorFallback>) => {
  return (
    <div className={styles.error}>
      <ErrorFallback
        {...props}
        title={NOTIFICATION_MESSAGES.DOMAIN_DETAIL_ERROR}
        description={NOTIFICATION_MESSAGES.DOMAIN_DETAIL_ERROR_DESC}
      />
    </div>
  );
};

export const DomainDetailContent = {
  Main,
  Error,
  Suspense,
};
